import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Download } from 'lucide-react';
import type { Upload } from '../../MediaTab';
import { sanitiseFileName } from '@/utils/sanitise';
import supabase from '@/utils/supabase';
import type { Database } from '@/types/database.types';
type Project = Database['public']['Tables']['projects']['Row'];
import { downloadZip } from 'client-zip';
import { useState } from 'react';
import { toast } from 'sonner';

type ManifestEntry = { id: string; name: string; url: string; size: number };

const BULK_DOWNLOAD_BROWSER_MESSAGE =
  'Bulk download requires a Chromium-based browser such as Chrome or Edge.';

function isManifestEntry(entry: unknown): entry is ManifestEntry {
  return (
    typeof entry === 'object' &&
    entry !== null &&
    typeof (entry as ManifestEntry).id === 'string' &&
    typeof (entry as ManifestEntry).name === 'string' &&
    typeof (entry as ManifestEntry).url === 'string' &&
    typeof (entry as ManifestEntry).size === 'number'
  );
}

function validateManifest(manifest: ManifestEntry[], attached: Upload[]): void {
  if (manifest.length !== attached.length) {
    throw new Error('Manifest length does not match attached uploads');
  }
  const manifestIds = new Set(manifest.map((entry) => entry.id));
  for (const upload of attached) {
    if (!manifestIds.has(upload.id)) {
      throw new Error(`Manifest missing upload ${upload.id}`);
    }
  }
}

function parseManifest(data: unknown): ManifestEntry[] {
  if (
    typeof data !== 'object' ||
    data === null ||
    !('manifest' in data) ||
    !Array.isArray((data as { manifest: unknown }).manifest) ||
    !(data as { manifest: unknown[] }).manifest.every(isManifestEntry)
  ) {
    throw new Error('Invalid media manifest received from server');
  }
  return (data as { manifest: ManifestEntry[] }).manifest;
}

export default function DownloadAllButton({
  uploads,
  project,
}: {
  uploads: Upload[];
  project: Project;
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const canDownloadAll = 'showSaveFilePicker' in window;
  const attachedUploadList = uploads.filter(
    (upload) => upload.status === 'attached'
  );
  const attachedUploads = attachedUploadList.length;

  async function handleAllDownload() {
    if (!canDownloadAll) {
      toast.info(BULK_DOWNLOAD_BROWSER_MESSAGE);
      return;
    }

    setIsDownloading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.error('No active session');
        toast.error('Please sign in to download.');
        return;
      }

      let fileHandle: FileSystemFileHandle;
      try {
        fileHandle = await window.showSaveFilePicker({
          suggestedName: `${sanitiseFileName(project.project_name)} - media.zip`,
          types: [
            {
              description: 'Media Zip File',
              accept: { 'application/zip': ['.zip'] },
            },
          ],
        });
      } catch (error) {
        if ((error as Error).name === 'AbortError') return;
        console.error('Error showing save file picker:', error);
        return;
      }

      let manifest: ManifestEntry[];
      try {
        const response = await fetch(
          `/api/team/projects/${project.id}/media/manifest`,
          {
            headers: {
              Authorization: `Bearer ${session?.access_token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error('Failed to get media manifest');
        }
        manifest = parseManifest(await response.json());
        validateManifest(manifest, attachedUploadList);
      } catch (error) {
        console.error('Error getting media manifest:', error);
        toast.error(
          error instanceof Error && error.message.startsWith('Manifest')
            ? 'Media list changed. Refresh the page and try again.'
            : 'Could not prepare download. Please try again.'
        );
        return;
      }

      let cancelRequested = false;
      let forceStopped = false;
      let wasTruncated = false;
      let truncatedFileName = '';
      const totalFiles = manifest.length;

      const toastId = toast.loading('Preparing download...', {
        duration: Infinity,
        action: { label: 'Cancel', onClick: requestCancel },
      });

      function requestCancel() {
        if (cancelRequested) return;
        cancelRequested = true;
        updateDownloadProgress();
      }

      function requestForceStop() {
        if (forceStopped) return;
        forceStopped = true;
        cancelRequested = true;
      }

      const totalSize = manifest.reduce((sum, file) => sum + file.size, 0);
      let sourceBytesRead = 0;
      let currentFileName = '';
      let currentFileSize = 0;
      let currentFileBytesRead = 0;
      let lastToastMessage = '';

      function updateDownloadProgress() {
        if (forceStopped) return;

        let message: string;
        let action: { label: string; onClick: () => void };

        if (cancelRequested) {
          const filePct =
            currentFileSize > 0
              ? Math.min(
                  100,
                  Math.round((currentFileBytesRead / currentFileSize) * 100)
                )
              : 0;
          message = currentFileName
            ? `Finishing ${currentFileName}: ${filePct}%`
            : 'Finishing current file, then stopping…';
          action = { label: 'Stop now', onClick: requestForceStop };
        } else if (totalSize <= 0) {
          message = currentFileName
            ? `Downloading... — ${currentFileName}`
            : 'Downloading...';
          action = { label: 'Cancel', onClick: requestCancel };
        } else {
          const pct = Math.min(
            100,
            Math.round((sourceBytesRead / totalSize) * 100)
          );
          message = currentFileName
            ? `${pct}% Downloading: ${currentFileName}`
            : `Downloading: ${pct}%`;
          action = { label: 'Cancel', onClick: requestCancel };
        }

        if (message === lastToastMessage) return;
        lastToastMessage = message;
        toast.loading(message, {
          id: toastId,
          duration: Infinity,
          action,
        });
      }

      let includedCount = 0;
      let failedCount = 0;

      function skipFailedFile(file: ManifestEntry, reason: unknown) {
        console.error('Download failed for:', file.name, reason);
        failedCount++;
        sourceBytesRead += file.size;
        updateDownloadProgress();
      }

      // Graceful cancel finishes the in-flight file before stopping. Force stop
      // terminates that entry's stream so client-zip closes it with bytes so far.
      async function* fileIterator() {
        for (const file of manifest) {
          if (cancelRequested) break;
          currentFileName = file.name;
          currentFileSize = file.size;
          currentFileBytesRead = 0;
          try {
            const res = await fetch(file.url);
            if (!res.ok) {
              skipFailedFile(file, res.status);
              continue;
            }
            if (!res.body) {
              skipFailedFile(file, 'empty body');
              continue;
            }
            includedCount++;
            const input = res.body.pipeThrough(
              new TransformStream({
                transform(chunk, ctrl) {
                  if (forceStopped) {
                    truncatedFileName = currentFileName;
                    wasTruncated = true;
                    ctrl.terminate();
                    return;
                  }
                  currentFileBytesRead += chunk.byteLength;
                  sourceBytesRead += chunk.byteLength;
                  updateDownloadProgress();
                  ctrl.enqueue(chunk);
                },
              })
            );
            yield { name: file.name, input };
          } catch (error) {
            skipFailedFile(file, error);
          }
        }
      }
      try {
        const writable = await fileHandle.createWritable();
        await downloadZip(fileIterator()).body!.pipeTo(writable);
        if (wasTruncated) {
          const savedCount = Math.max(0, includedCount - 1);
          toast.info(
            `Stopped. Saved ${savedCount} file${savedCount === 1 ? '' : 's'}. 1 file (${truncatedFileName}) is incomplete.`,
            { id: toastId, duration: 8000, action: undefined }
          );
        } else if (cancelRequested) {
          toast.info(
            `Download stopped. Saved ${includedCount} of ${totalFiles} file${totalFiles === 1 ? '' : 's'}`,
            { id: toastId, duration: 6000, action: undefined }
          );
        } else if (includedCount === 0) {
          toast.error('Download failed', {
            id: toastId,
            duration: 4000,
            action: undefined,
          });
        } else if (failedCount > 0) {
          toast.success(
            `Download completed (${failedCount} file${failedCount === 1 ? '' : 's'} skipped)`,
            { id: toastId, duration: 6000, action: undefined }
          );
        } else {
          toast.success('Download completed', {
            id: toastId,
            duration: 4000,
            action: undefined,
          });
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          toast.info('Download cancelled', {
            id: toastId,
            duration: 6000,
            action: undefined,
          });
          return;
        }
        toast.error('Download failed', {
          id: toastId,
          duration: 4000,
          action: undefined,
        });
      }
    } finally {
      setIsDownloading(false);
    }
  }

  const button = (
    <Button
      size={'sm'}
      variant={'ghost'}
      className="cursor-pointer"
      onClick={handleAllDownload}
      disabled={attachedUploads === 0 || isDownloading}
    >
      <Download /> Download All
    </Button>
  );

  if (!canDownloadAll && attachedUploads > 0) {
    return (
      <Tooltip delayDuration={800}>
        <TooltipTrigger asChild>
          <span className="inline-flex">{button}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{BULK_DOWNLOAD_BROWSER_MESSAGE}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
}
